import React, { useMemo } from "react";
import Button from "@atlaskit/button/new";

const ROOT_ID = "";

const normalizeParentId = (value) => (typeof value === "string" ? value : "");

const sanitizeActions = (actions) => {
  if (!Array.isArray(actions)) {
    return [];
  }

  const actionIds = new Set(actions.map((action) => action?.id).filter((id) => typeof id === "string" && id.length > 0));

  return actions
    .map((action, index) => {
      if (!action?.id) {
        return null;
      }

      const parentId = normalizeParentId(action.parentId);
      const hasValidParent = parentId.length > 0 && parentId !== action.id && actionIds.has(parentId);
      const order = typeof action.order === "number" ? action.order : Number.MAX_SAFE_INTEGER;

      return {
        ...action,
        parentId: hasValidParent ? parentId : ROOT_ID,
        order,
        __index: index,
      };
    })
    .filter(Boolean);
};

const buildStructure = (actions) => {
  const byId = {};
  const children = {};

  actions.forEach((action) => {
    byId[action.id] = { ...action };
    const parentId = action.parentId || ROOT_ID;
    if (!children[parentId]) {
      children[parentId] = [];
    }
    children[parentId].push(action.id);
  });

  Object.keys(children).forEach((parentId) => {
    children[parentId].sort((a, b) => {
      const first = byId[a];
      const second = byId[b];
      if (first.order !== second.order) {
        return first.order - second.order;
      }
      return first.__index - second.__index;
    });
  });

  return { byId, children };
};

const flattenRows = (byId, children, parentId = ROOT_ID, depth = 0, rows = []) => {
  const childIds = children[parentId] || [];
  childIds.forEach((id, index) => {
    rows.push({ id, depth, parentId, siblingIndex: index, siblingCount: childIds.length });
    flattenRows(byId, children, id, depth + 1, rows);
  });
  return rows;
};

const toArrayWithNewOrder = (originalActions, byId, children) => {
  const orderedIds = flattenRows(byId, children).map((row) => row.id);
  const orderById = orderedIds.reduce((acc, id, index) => {
    acc[id] = (index + 1) * 100;
    return acc;
  }, {});

  return originalActions.map((action) => {
    if (!action?.id || !byId[action.id]) {
      return action;
    }
    return {
      ...action,
      parentId: byId[action.id].parentId || ROOT_ID,
      order: orderById[action.id] ?? action.order,
    };
  });
};

const moveInStructure = (actions, actionId, moveType) => {
  const sanitized = sanitizeActions(actions);
  const { byId, children } = buildStructure(sanitized);
  const currentAction = byId[actionId];

  if (!currentAction) {
    return actions;
  }

  const currentParentId = currentAction.parentId || ROOT_ID;
  const siblings = children[currentParentId] || [];
  const currentIndex = siblings.indexOf(actionId);

  if (currentIndex < 0) {
    return actions;
  }

  if (moveType === "up") {
    if (currentIndex === 0) {
      return actions;
    }
    [siblings[currentIndex - 1], siblings[currentIndex]] = [siblings[currentIndex], siblings[currentIndex - 1]];
  }

  if (moveType === "down") {
    if (currentIndex >= siblings.length - 1) {
      return actions;
    }
    [siblings[currentIndex], siblings[currentIndex + 1]] = [siblings[currentIndex + 1], siblings[currentIndex]];
  }

  if (moveType === "indent") {
    if (currentIndex === 0) {
      return actions;
    }

    const newParentId = siblings[currentIndex - 1];
    siblings.splice(currentIndex, 1);

    if (!children[newParentId]) {
      children[newParentId] = [];
    }
    children[newParentId].push(actionId);
    byId[actionId].parentId = newParentId;
  }

  if (moveType === "outdent") {
    if (!currentParentId) {
      return actions;
    }

    const parentAction = byId[currentParentId];
    const newParentId = parentAction?.parentId || ROOT_ID;
    const newSiblings = children[newParentId] || [];
    const parentIndex = newSiblings.indexOf(currentParentId);

    siblings.splice(currentIndex, 1);
    newSiblings.splice(parentIndex + 1, 0, actionId);
    children[newParentId] = newSiblings;
    byId[actionId].parentId = newParentId;
  }

  return toArrayWithNewOrder(actions, byId, children);
};

const OrderActionList = ({ actions, onChange }) => {
  const sanitizedActions = useMemo(() => sanitizeActions(actions), [actions]);
  const structure = useMemo(() => buildStructure(sanitizedActions), [sanitizedActions]);
  const rows = useMemo(() => flattenRows(structure.byId, structure.children), [structure]);

  const onMove = (actionId, moveType) => {
    const nextActions = moveInStructure(actions, actionId, moveType);
    onChange(nextActions);
  };

  if (sanitizedActions.length === 0) {
    return <div className="ihub-action-tree-empty">No actions yet</div>;
  }

  return (
    <div className="ihub-order-action-list">
      {rows.map((row) => {
        const action = structure.byId[row.id];
        const actionName = action?.name || "Untitled action";
        return (
          <div key={row.id} className="ihub-order-action-list-row">
            <div className="ihub-order-action-list-name" style={{ paddingLeft: `${row.depth * 16}px` }}>
              <span className="ihub-order-action-list-level">L{row.depth + 1}</span>
              {actionName}
            </div>
            <div className="ihub-order-action-list-controls">
              <Button appearance="subtle" spacing="compact" onClick={() => onMove(row.id, "up")} isDisabled={row.siblingIndex === 0}>
                Up
              </Button>
              <Button
                appearance="subtle"
                spacing="compact"
                onClick={() => onMove(row.id, "down")}
                isDisabled={row.siblingIndex >= row.siblingCount - 1}
              >
                Down
              </Button>
              <Button appearance="subtle" spacing="compact" onClick={() => onMove(row.id, "indent")} isDisabled={row.siblingIndex === 0}>
                Indent
              </Button>
              <Button appearance="subtle" spacing="compact" onClick={() => onMove(row.id, "outdent")} isDisabled={row.parentId === ROOT_ID}>
                Outdent
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default OrderActionList;
